import {InlineAutocomplete} from '@github-ui/inline-autocomplete'
import type {ShowSuggestionsEvent, Suggestion} from '@github-ui/inline-autocomplete/types'
import {NoteIcon, PencilIcon, SlidersIcon} from '@primer/octicons-react'
import {ActionList, ActionMenu, Box, Button, FormControl, IconButton, PageLayout, Text, Textarea} from '@primer/react'
import {Blankslate} from '@primer/react/experimental'
import {useCallback, useState} from 'react'
import type {ModelState} from '../../../types'
import {Panel} from '../../../utils/playground-manager'
import {GiveFeedback} from '../../playground/components/GettingStartedDialog/GiveFeedback'
import ModelParameters from '../../playground/components/ModelParameters'
import {PlaygroundChatMessage} from '../../playground/components/PlaygroundChatMessage'
import {Header} from './Header'

export type PromptProps = {
  modelState: ModelState
  canUseO1Models: boolean
}

export function Prompt({modelState, canUseO1Models}: PromptProps) {
  const {gettingStarted, modelInputSchema, catalogData, messages} = modelState
  const lastIndex = messages.length - 1

  const [variableSuggestions, setVariableSuggestions] = useState<Suggestion[]>([])
  const onShowSuggestions = useCallback((_event: ShowSuggestionsEvent) => {
    // TODO: Get this from evals state, or separate Variables UI
    setVariableSuggestions(['{{input}}', '{{expected}}'])
  }, [])
  const onHideSuggestions = useCallback(() => {
    setVariableSuggestions([])
  }, [])

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        height: 'calc(100dvh - 64px)',
        flex: 1,
      }}
    >
      <Box
        sx={{
          display: ['block', 'block', 'none'],
        }}
      >
        <GiveFeedback mobile />
      </Box>
      <Box sx={{height: '100%', width: '100%', p: 3, overflow: 'auto'}}>
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            flex: 1,
            width: '100%',
            height: '100%',
          }}
        >
          <Box
            sx={{
              flex: 1,
              display: 'flex',
              flexDirection: 'column',
              width: '100%',
              maxHeight: '100%',
              height: '100%',
            }}
          >
            <Header
              run={() => {}}
              running={false}
              model={catalogData}
              modelInputSchema={modelInputSchema}
              canUseO1Models={canUseO1Models}
              gettingStarted={gettingStarted}
            />
            <div className={`flex-1 d-flex flex-md-row border overflow-hidden rounded-2`}>
              <PageLayout
                padding="none"
                containerWidth="full"
                columnGap="none"
                sx={{
                  flexGrow: 1,
                  height: 'calc(100dvh - 64px)',
                }}
              >
                <PageLayout.Pane resizable position="start" divider="none" padding="none">
                  <div className="d-flex flex-justify-between position-sticky top-0 p-2 border-bottom bgColor-muted flex-items-center">
                    <Box sx={{flexGrow: 1}} />
                    <Button size="small" leadingVisual={PencilIcon} sx={{mr: 2}}>
                      Variables
                    </Button>
                    <ActionMenu
                      onOpenChange={(isOpen: boolean) => {
                        const {parameters: schema = []} = modelState.modelInputSchema || {}
                        if (isOpen || schema.length === 0 || !modelState.parametersHasChanges) return

                        // // Ensure validation when menu is closed, as it may not have occurred yet
                        // for (const [index, {modelInputSchema = {}, parameters}] of models.entries()) {
                        //   if (syncInputs || index === position) {
                        //     manager.setParameters(
                        //       index,
                        //       validateAndFilterParameters(modelInputSchema.parameters || [], parameters),
                        //     )
                        //   }
                        // }
                      }}
                    >
                      <ActionMenu.Anchor>
                        <IconButton icon={SlidersIcon} size="small" aria-label="Show parameters setting" />
                      </ActionMenu.Anchor>
                      <ActionMenu.Overlay width="xlarge" side="outside-left">
                        <ActionList>
                          <div
                            className="d-flex flex-items-center border-bottom borderColor-default px-3 py-2"
                            style={{minHeight: '49px'}} // This is to prevent the height from changing when the reset button appears
                          >
                            <Text as="h1" sx={{fontSize: 1, fontWeight: 'bold', flex: 1}}>
                              Parameters
                            </Text>
                          </div>
                          <ModelParameters model={modelState} position={Panel.Main} />
                        </ActionList>
                      </ActionMenu.Overlay>
                    </ActionMenu>
                  </div>

                  <Box as="form" sx={{p: 3}}>
                    <FormControl sx={{mb: 3}}>
                      <FormControl.Label>System</FormControl.Label>
                      <Textarea resize="vertical" block placeholder="You are a helpful assistant..." rows={2} />
                    </FormControl>

                    <FormControl sx={{mb: 3}}>
                      <FormControl.Label>User</FormControl.Label>
                      <InlineAutocomplete
                        fullWidth
                        tabInsertsSuggestions
                        triggers={[
                          {
                            triggerChar: '{{',
                            keepTriggerCharOnCommit: false,
                          },
                        ]}
                        suggestions={variableSuggestions}
                        onShowSuggestions={onShowSuggestions}
                        onHideSuggestions={onHideSuggestions}
                      >
                        <Textarea
                          resize="vertical"
                          placeholder="Enter a user prompt. Add variables with {{variable}}"
                          block
                        />
                      </InlineAutocomplete>
                    </FormControl>
                  </Box>
                </PageLayout.Pane>
                <PageLayout.Content
                  as="div"
                  sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    height: 'calc(100dvh - 64px)',
                  }}
                >
                  {messages?.length > 0 ? (
                    <div className="position-absolute p-3">
                      {messages.map((message, index) => (
                        <PlaygroundChatMessage
                          model={modelState}
                          message={message}
                          index={index}
                          key={`${message.timestamp}-${message.message}`}
                          isLoading={false /*index === lastIndex && isLoading*/}
                          isError={message.role === 'error'}
                          lastIndex={index === lastIndex}
                          handleRegenerate={() => {}}
                          handleClearHistory={() => {}}
                        />
                      ))}
                    </div>
                  ) : (
                    <Blankslate>
                      <Blankslate.Visual>
                        <NoteIcon size="medium" />
                      </Blankslate.Visual>
                      <Blankslate.Heading>Iterate on your prompt</Blankslate.Heading>
                      <Blankslate.Description>
                        Run prompt to see response from {modelState.catalogData.name}
                      </Blankslate.Description>
                    </Blankslate>
                  )}
                </PageLayout.Content>
              </PageLayout>
            </div>
          </Box>
        </Box>
      </Box>
    </Box>
  )
}

try{ Prompt.displayName ||= 'Prompt' } catch {}